import {MutableRefObject, useEffect, useRef, useState} from 'react';
import './daily-care-log.screen.scss';
import {Route, Switch, useRouteMatch, Redirect, useHistory} from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  Container,
  IconButton,
  Typography,
  styled,
  useTheme,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import {useAuth} from '../../../../contexts/auth/context';
import {useAppContext} from '../../../../contexts/app/context';
import {useDailyCareLog} from '../../../../contexts/daily-care-log/context';
import UserMenu from '../../../../components/user-menu/user-menu';
import {ProductLogo} from '../../../../components/product-logo/product-logo';
import {AlertSnackbar} from '../../../../components/daily-care-log/alert-snackbar';
import {ScreenPaths} from '../../../../enums/screen-path';
import {ThemeMode} from '../../../../enums';
import {appSizes} from '../../../../utils/constants';
import {DailyCareLogFiltersContextProvider} from '../../../../contexts/dailycare-log-filters/provider';
import HomeDailyCareLogScreen from '../home/home.daily-care-log.screen';
import SeniorTasksDailyCareLogScreen from '../senior-tasks/senior-tasks.daily-care-log.screen';
import ShiftDetailsCareLogScreen from '../shift-details/shift-details.screen';
import TaskDetailsDailyCareLogScreen from '../task-details/task-details.daily-care-log.screen';
import GroupedTasksDetailsCareLogScreen from '../grouped-tasks-details/grouped-tasks-details.daily-care-log.screen';
import GroupedTasksHandlingScreen from '../grouped-task-handling/grouped-tasks-handling.screen';
import PermissionsRequiredRoute from '../../../../components/permissions-required-route';
import {RolePermissionType} from '../../../../backend/enums/role-permission-type';
import AttendanceSessionDetailsDailyCareLogScreen from '../attendance-tracker/attendance-session-details.daily-care-log.screen';
import NewAttendanceRecordDailyCareLogScreen from '../attendance-tracker/new-attendance-record.daily-care-log.screen';
import AttendanceTrackerDailyCareLogScreen from '../attendance-tracker/attendance-tracker.daily-care-log.screen';
import OccurrencesDailyCareLogScreen from '../occurrences/occurrences.daily-care-log.screen';
import {AddOccurrencesDailyCareLogScreen} from '../occurrences/add-occurrence.daily-care-log.screen';
import {UnplannedTaskDailyCareLogScreen} from '../unplanned-tasks/unplannedtask.daily-care-log.screen';
import AccessRequiredRoute from '../../../../components/access-required-route';
import HandleOccurrenceDailyCareLogScreen from '../occurrences/handle-occurrence.daily-care-log.screen';
import {UserType} from '../../../../backend/enums/user-type';
import {DailyCareLogFooter} from './daily-care-log-footer';
import {AttendanceTrackerStatusProvider} from '../../../../contexts/attendance-tracker-status/provider';
import {useCanTrackAttendance} from '../../../../hooks/use-can-track-attendance';
import {useFetchEntityUsers} from '../../../../hooks/use-fetch-entity-users';
import {getOccurrencePath} from '../../../../utils/data/occurrences';
import {useOffice} from '../../../../contexts/office/context';
import {useEntity} from '../../../../contexts/entity/context';

const AppBarOffset = styled('div')(() => ({height: `${appSizes.dailyCareLog.headerHeight}px`}));

// Room for the bottom navigation and for the attendance bar above it (when it is shown)
const BottomBarOffset = styled('div')(() => ({
  height: `calc(${appSizes.dailyCareLog.footerHeight}px + var(--attendance-bar-height, 0px))`,
}));

function DailyCareLogContent() {
  const appContext = useAppContext();
  const {
    state: {session, firebaseUser},
    hasReadPermission,
  } = useAuth();
  const dailyCareLogContext = useDailyCareLog();
  const canTrackAttendance = useCanTrackAttendance();
  const officeContext = useOffice();
  const {updateEntityOccurrencesCount} = useEntity();
  const theme = useTheme();
  let {path} = useRouteMatch();
  let history = useHistory();
  const selectedEntityIdRef: MutableRefObject<string | undefined> = useRef(
    session?.selectedAffiliation?.entity.id
  );
  const [bottomNavigationValue, setBottomNavigationValue] = useState<number>(0);
  const {items: seniors} = useFetchEntityUsers({
    activeUsers: true,
    entityId: session?.selectedAffiliation?.entity.id,
  });
  const {items: professionals} = useFetchEntityUsers({
    userType: UserType.Caregiver,
    entityId: session?.selectedAffiliation?.entity.id,
  });

  useEffect(() => {
    if (history.location.pathname.includes('occurrences')) {
      setBottomNavigationValue(1);
    } else if (history.location.pathname.includes(ScreenPaths.DailyCareLog_AttendanceTracker)) {
      setBottomNavigationValue(2);
    }
  }, [history.location.pathname]);

  // Update ref
  const updateSelectedEntityIdRef = () => {
    // Update it
    selectedEntityIdRef.current = session?.selectedAffiliation?.entity.id;
  };

  useEffect(() => {
    // when user clicks on occurrence notification and the website is in background
    if (officeContext.state.occurrence) {
      updateEntityOccurrencesCount();

      const path = getOccurrencePath(
        'daily-care-log',
        officeContext.state.occurrence.entityId,
        officeContext.state.occurrence.id,
        officeContext.state.occurrence.date,
        hasReadPermission,
        officeContext.state.occurrence.appAlreadyOpened
      );
      history.push(path);
      officeContext.clearOccurrence();
    }
  }, [
    hasReadPermission,
    history,
    officeContext,
    officeContext.state.occurrence,
    updateEntityOccurrencesCount,
  ]);

  // Build
  return (
    <Box className="daily-care-log-screen">
      <CssBaseline />

      {/* Toast notifications */}
      <AlertSnackbar />

      <AppBar
        className="app-bar"
        color="primary"
        sx={{
          maxHeight: appSizes.dailyCareLog.headerHeight,
          backgroundColor:
            appContext.state.currentThemeMode === ThemeMode.Light
              ? theme.palette.background.paper
              : theme.palette.grey[900],
        }}
      >
        {dailyCareLogContext.state.navigationHeader.active && (
          <Box className="app-bar-back-navigation" maxHeight={appSizes.dailyCareLog.headerHeight}>
            <IconButton
              className="back-button"
              onClick={() => {
                if (history.length > 0) {
                  history.goBack();
                } else {
                  // go to the occurrences page after going to handle occurrences on click occurrence notification
                  history.push(
                    `${path}/${session?.selectedAffiliation?.entity.id}${ScreenPaths.DailyCareLog_Occurrences}`
                  );
                }
              }}
            >
              <ArrowBackIosNewIcon />
            </IconButton>
            <Typography className="back-title">
              {dailyCareLogContext.state.navigationHeader.text}
            </Typography>
          </Box>
        )}

        {!dailyCareLogContext.state.navigationHeader.active && (
          <Box className="app-bar-logo">
            <ProductLogo
              usePrimary={false}
              height={appContext.state.currentThemeSettings.homeLogoHeight}
            />
            {firebaseUser && <UserMenu />}
          </Box>
        )}
      </AppBar>
      {/* Accounts for the fixed positioning of the app bar */}
      <AppBarOffset />
      <DailyCareLogFiltersContextProvider users={seniors}>
        <Container component="main">
          <Switch>
            {/* Home */}
            <PermissionsRequiredRoute
              exact
              path={`${path}/:entityId${ScreenPaths.DailyCareLog_Home}`}
              requiredPermissions={[
                {
                  type: RolePermissionType.DailyCareLog,
                  canRead: true, // users must be able to at least read daily care log to access the page
                },
              ]}
              render={({
                match: {
                  params: {entityId},
                },
              }) => {
                if (entityId === session?.selectedAffiliation?.entity.id) {
                  // Update it
                  updateSelectedEntityIdRef();

                  // Nothing changed
                  return <HomeDailyCareLogScreen seniors={seniors} />;
                } else {
                  // Update it
                  updateSelectedEntityIdRef();

                  // Updating the entity id in the path.
                  return (
                    <Redirect
                      to={`${path}/${session?.selectedAffiliation?.entity.id}${ScreenPaths.DailyCareLog_Home}`}
                    />
                  );
                }
              }}
            />

            {/* Senior Tasks */}
            <PermissionsRequiredRoute
              path={`${path}/:entityId${ScreenPaths.DailyCareLog_SeniorTasks}`}
              requiredPermissions={[
                {
                  type: RolePermissionType.DailyCareLog,
                  canRead: true, // users must be able to at least read daily care log to access the page
                },
              ]}
              render={({
                match: {
                  params: {entityId},
                },
              }) => {
                if (entityId === session?.selectedAffiliation?.entity.id) {
                  // Update it
                  updateSelectedEntityIdRef();

                  // Nothing changed
                  return <SeniorTasksDailyCareLogScreen />;
                } else {
                  // Update it
                  updateSelectedEntityIdRef();

                  // Updating the entity id in the path.
                  return (
                    <Redirect
                      to={`${path}/${session?.selectedAffiliation?.entity.id}${ScreenPaths.DailyCareLog_SeniorTasks}`}
                    />
                  );
                }
              }}
            />

            {/* Shift details */}
            <PermissionsRequiredRoute
              exact
              path={`${path}/:entityId${ScreenPaths.DailyCareLog_Shift}/:entityShiftId`}
              requiredPermissions={[
                {
                  type: RolePermissionType.DailyCareLog,
                  canRead: true, // users must be able to at least read daily care log to access the page
                },
              ]}
              render={({
                match: {
                  params: {entityId},
                },
              }) => {
                if (entityId === session?.selectedAffiliation?.entity.id) {
                  // Update it
                  updateSelectedEntityIdRef();

                  // Nothing changed
                  return <ShiftDetailsCareLogScreen users={seniors} />;
                } else {
                  // Update it
                  updateSelectedEntityIdRef();

                  // Updating the entity id in the path.
                  return (
                    <Redirect
                      to={`${path}/${session?.selectedAffiliation?.entity.id}${ScreenPaths.DailyCareLog_Shift}/:entityShiftId`}
                    />
                  );
                }
              }}
            />

            {/* Grouped tasks details */}
            <PermissionsRequiredRoute
              exact
              path={`${path}/:entityId${ScreenPaths.DailyCareLog_Shift}/:entityShiftId${ScreenPaths.DailyCareLog_Grouped_Tasks}`}
              requiredPermissions={[
                {
                  type: RolePermissionType.DailyCareLog,
                  canRead: true, // users must be able to at least read daily care log to access the page
                },
              ]}
              render={({
                match: {
                  params: {entityId},
                },
              }) => {
                if (entityId === session?.selectedAffiliation?.entity.id) {
                  // Update it
                  updateSelectedEntityIdRef();

                  // Nothing changed
                  return <GroupedTasksDetailsCareLogScreen users={seniors} />;
                } else {
                  // Update it
                  updateSelectedEntityIdRef();

                  // Updating the entity id in the path.
                  return (
                    <Redirect
                      to={`${path}/${session?.selectedAffiliation?.entity.id}${ScreenPaths.DailyCareLog_Shift}/:entityShiftId${ScreenPaths.DailyCareLog_Grouped_Tasks}`}
                    />
                  );
                }
              }}
            />

            {/* Grouped tasks details - edit */}
            <PermissionsRequiredRoute
              path={`${path}/:entityId${ScreenPaths.DailyCareLog_Shift}/:entityShiftId${ScreenPaths.DailyCareLog_Grouped_Tasks}/handle`}
              requiredPermissions={[
                {
                  type: RolePermissionType.DailyCareLog,
                  canWrite: true, // users must be able to write daily care log to access the page
                },
              ]}
              render={({
                match: {
                  params: {entityId},
                },
              }) => {
                if (entityId === session?.selectedAffiliation?.entity.id) {
                  // Update it
                  updateSelectedEntityIdRef();

                  // Nothing changed
                  return <GroupedTasksHandlingScreen />;
                } else {
                  // Update it
                  updateSelectedEntityIdRef();

                  // Updating the entity id in the path.
                  return (
                    <Redirect
                      to={`${path}/${session?.selectedAffiliation?.entity.id}/:entityShiftId${ScreenPaths.DailyCareLog_Grouped_Tasks}/handle`}
                    />
                  );
                }
              }}
            />

            {/* Task details */}
            <PermissionsRequiredRoute
              exact
              path={`${path}/:entityId${ScreenPaths.DailyCareLog_Tasks}/:careTaskRef`}
              requiredPermissions={[
                {
                  type: RolePermissionType.DailyCareLog,
                  canRead: true, // users must be able to at least read daily care log to access the page
                },
              ]}
              render={({
                match: {
                  params: {entityId},
                },
              }) => {
                if (entityId === session?.selectedAffiliation?.entity.id) {
                  // Update it
                  updateSelectedEntityIdRef();

                  // Nothing changed
                  return <TaskDetailsDailyCareLogScreen />;
                } else {
                  // Update it
                  updateSelectedEntityIdRef();

                  // Updating the entity id in the path.
                  return (
                    <Redirect
                      to={`${path}/${session?.selectedAffiliation?.entity.id}${ScreenPaths.DailyCareLog_Tasks}/:careTaskRef`}
                    />
                  );
                }
              }}
            />

            {/** Occurrences */}
            <AccessRequiredRoute
              access="care_management"
              path={`${path}/:entityId${ScreenPaths.DailyCareLog_Occurrences}`}
            >
              <Switch>
                <PermissionsRequiredRoute
                  exact
                  path={`${path}/:entityId${ScreenPaths.DailyCareLog_Occurrences}`}
                  requiredPermissions={[
                    {
                      type: RolePermissionType.DailyCareLog_Occurrences,
                      canRead: true, // users must be able to at least read daily care log to access the page
                    },
                  ]}
                  render={({
                    match: {
                      params: {entityId},
                    },
                  }) => {
                    if (entityId === session?.selectedAffiliation?.entity.id) {
                      updateSelectedEntityIdRef();

                      return (
                        <OccurrencesDailyCareLogScreen
                          seniors={seniors}
                          professionals={professionals}
                        />
                      );
                    } else {
                      updateSelectedEntityIdRef();

                      return (
                        <Redirect
                          to={`${path}/${session?.selectedAffiliation?.entity.id}${ScreenPaths.DailyCareLog_Occurrences}`}
                        />
                      );
                    }
                  }}
                />
                <PermissionsRequiredRoute
                  exact
                  requiredPermissions={[
                    {
                      type: RolePermissionType.DailyCareLog_Occurrences,
                      canRead: true,
                      canWrite: true,
                    },
                  ]}
                  path={`${path}/:entityId${ScreenPaths.DailyCareLog_NewOccurrence}`}
                  render={({
                    match: {
                      params: {entityId},
                    },
                  }) => {
                    if (entityId === session?.selectedAffiliation?.entity.id) {
                      updateSelectedEntityIdRef();

                      return (
                        <AddOccurrencesDailyCareLogScreen
                          seniors={seniors}
                          professionals={professionals?.filter(
                            (professional) => professional.user.isActive
                          )}
                        />
                      );
                    } else {
                      updateSelectedEntityIdRef();

                      return (
                        <Redirect
                          to={`${path}/${session?.selectedAffiliation?.entity.id}${ScreenPaths.DailyCareLog_NewOccurrence}`}
                        />
                      );
                    }
                  }}
                />
                <PermissionsRequiredRoute
                  exact
                  requiredPermissions={[
                    {
                      type: RolePermissionType.DailyCareLog_Occurrences,
                      canRead: true,
                    },
                  ]}
                  path={`${path}/:entityId${ScreenPaths.DailyCareLog_HandleOccurrence}`}
                  render={({
                    match: {
                      params: {entityId, occurrenceId},
                    },
                  }) => {
                    if (entityId === session?.selectedAffiliation?.entity.id) {
                      updateSelectedEntityIdRef();

                      return (
                        <HandleOccurrenceDailyCareLogScreen
                          professionals={professionals}
                          seniors={seniors}
                        />
                      );
                    } else {
                      updateSelectedEntityIdRef();

                      return (
                        <Redirect
                          to={`${path}/${session?.selectedAffiliation?.entity.id}${ScreenPaths.DailyCareLog_Occurrences}/${occurrenceId}`}
                        />
                      );
                    }
                  }}
                />
                {/*  Default route */}

                <Route path={path}>
                  <Redirect
                    to={`${path}/${session?.selectedAffiliation?.entity.id}${ScreenPaths.DailyCareLog_Occurrences}`}
                  />
                </Route>
              </Switch>
            </AccessRequiredRoute>

            {/* Unplanned task */}
            <PermissionsRequiredRoute
              exact
              path={`${path}/:entityId${ScreenPaths.DailyCareLog_Shift}/:entityShiftId/new`}
              requiredPermissions={[
                {
                  type: RolePermissionType.DailyCareLog,
                  canWrite: true,
                  canRead: true, // users must be able to at least read daily care log to access the page
                },
              ]}
              render={({
                match: {
                  params: {entityId},
                },
              }) => {
                if (entityId === session?.selectedAffiliation?.entity.id) {
                  // Update it
                  updateSelectedEntityIdRef();

                  // Nothing changed
                  return <UnplannedTaskDailyCareLogScreen users={seniors} />;
                } else {
                  // Update it
                  updateSelectedEntityIdRef();

                  // Updating the entity id in the path.
                  return (
                    <Redirect
                      to={`${path}/${session?.selectedAffiliation?.entity.id}${ScreenPaths.DailyCareLog_Shift}/:entityShiftId/new`}
                    />
                  );
                }
              }}
            />

            {/** Attendance tracker */}
            {canTrackAttendance && (
              <Route
                exact
                path={`${path}/:entityId${ScreenPaths.DailyCareLog_AttendanceTracker}`}
                render={({
                  match: {
                    params: {entityId},
                  },
                }) => {
                  if (entityId === session?.selectedAffiliation?.entity.id) {
                    updateSelectedEntityIdRef();

                    return <AttendanceTrackerDailyCareLogScreen />;
                  } else {
                    updateSelectedEntityIdRef();

                    return (
                      <Redirect
                        to={`${path}/${session?.selectedAffiliation?.entity.id}${ScreenPaths.DailyCareLog_AttendanceTracker}`}
                      />
                    );
                  }
                }}
              />
            )}

            {canTrackAttendance && (
              <Route
                exact
                path={`${path}/:entityId${ScreenPaths.DailyCareLog_NewAttendanceRecord}`}
                render={({
                  match: {
                    params: {entityId},
                  },
                }) => {
                  if (entityId === session?.selectedAffiliation?.entity.id) {
                    updateSelectedEntityIdRef();

                    return <NewAttendanceRecordDailyCareLogScreen />;
                  } else {
                    updateSelectedEntityIdRef();

                    return (
                      <Redirect
                        to={`${path}/${session?.selectedAffiliation?.entity.id}${ScreenPaths.DailyCareLog_NewAttendanceRecord}`}
                      />
                    );
                  }
                }}
              />
            )}

            {/* Declared after the new record, which would be read as the id of a session */}
            {canTrackAttendance && (
              <Route
                exact
                path={`${path}/:entityId${ScreenPaths.DailyCareLog_AttendanceTrackerSession}`}
                render={({
                  match: {
                    params: {entityId},
                  },
                }) => {
                  if (entityId === session?.selectedAffiliation?.entity.id) {
                    updateSelectedEntityIdRef();

                    return <AttendanceSessionDetailsDailyCareLogScreen />;
                  } else {
                    updateSelectedEntityIdRef();

                    return (
                      <Redirect
                        to={`${path}/${session?.selectedAffiliation?.entity.id}${ScreenPaths.DailyCareLog_AttendanceTracker}`}
                      />
                    );
                  }
                }}
              />
            )}

            {/** Default route */}
            <Route path={path}>
              <Redirect
                to={`${path}/${session?.selectedAffiliation?.entity.id}${ScreenPaths.DailyCareLog_Home}`}
              />
            </Route>
          </Switch>

          <BottomBarOffset />
        </Container>

        {dailyCareLogContext.state.bottomNavigation.visible && (
          <DailyCareLogFooter
            bottomNavigationValue={bottomNavigationValue}
            setBottomNavigationValue={setBottomNavigationValue}
          />
        )}
      </DailyCareLogFiltersContextProvider>
    </Box>
  );
}

export default function DailyCareLogScreen() {
  return (
    <AttendanceTrackerStatusProvider>
      <DailyCareLogContent />
    </AttendanceTrackerStatusProvider>
  );
}
